import { useAppDispatch, useUser } from '../../../app/hooks';
import { debounceInComponent } from '../../../components/miscUtils';
import { getAssetTypeName } from '../../../data/assets/helpers';
import { setSearchText, setTypeFilter } from '../../../data/assets/queriedAssetsSlice';
import { useState, useEffect } from 'react';
import styles from '../StartPage.module.scss';

interface TpSearchFilterProps {
  ontology: any;
  queryStats: { [key: string]: number };
  searchText: string;
  typeFilter: string;
  dashboardtemplate: any;
}

const ORDER_ALPHABETICAL = 'alphabetical';
const ORDER_COUNT = 'count';
const DEFAULT_ORDER = ORDER_ALPHABETICAL;

const SearchFilter = (props: TpSearchFilterProps) => {
  const { ontology, queryStats, typeFilter, dashboardtemplate } = props;
  const dispatch = useAppDispatch();
  const { user } = useUser();

  const [localSearchText, setLocalSearchText] = useState(props.searchText);

  const debouncedSetSearchText = debounceInComponent((arg: string) => {
    dispatch(setSearchText({ searchText: arg }));
  }, 300);

  // Filter and order the displayed query stats based on dashboard template settings
  const primaryOrder = dashboardtemplate.tagOptions?.order || DEFAULT_ORDER;
  const displayedQueryStats = Object.entries(queryStats)
    .concat(
      // optionally add type from the filter if not present in queryStats
      typeFilter && !Object.keys(queryStats).includes(typeFilter) ? [[typeFilter, 0]] : [],
    ) // optionally add typeFilter if not present in queryStats
    .filter(([assetType, count]) => {
      return assetType !== 'num_of_items' && !!ontology.assetTypes[assetType];
    })
    .map(([assetType, count]) => {
      const displayName = getAssetTypeName(ontology.assetTypes[assetType], count > 1) ?? '';
      return [assetType, count, displayName] as [string, number, string];
    })
    .sort(([assetTypeA, countA, nameA], [assetTypeB, countB, nameB]) => {
      if (primaryOrder === ORDER_ALPHABETICAL) {
        return nameA.localeCompare(nameB) || countB - countA;
      } else if (primaryOrder === ORDER_COUNT) {
        return countB - countA || nameA.localeCompare(nameB);
      }
      console.warn(`Unknown tag order option: ${primaryOrder}`);
      return nameA.localeCompare(nameB) || countB - countA;
    });

  // keep local state in sync when parent/Redux updates the searchText prop
  useEffect(() => {
    setLocalSearchText(props.searchText);
  }, [props.searchText]);

  return (
    <>
      <div className={styles.searchArea}>
        {!dashboardtemplate.disableSearchBar && (
          <>
            <div>
              {dashboardtemplate.searchBarOptions.label}
              <p />
            </div>

            <input
              value={localSearchText}
              id="startPageSearch"
              onChange={(ev) => {
                setLocalSearchText(ev.target.value);
                debouncedSetSearchText(ev.target.value);
              }}
            />
          </>
        )}
        {!dashboardtemplate.disableTags && Object.values(ontology.assetTypes).length > 0 && (
          <div className={styles.assetTypeWrapper}>
            {displayedQueryStats.map(([assetType, count, displayName]) => (
              <div
                key={assetType}
                className={!typeFilter || typeFilter === assetType ? styles.assetTypeTagActive : styles.assetTypeTag}
                onClick={() => {
                  let newFilter = '';
                  if (assetType !== typeFilter) newFilter = assetType;
                  dispatch(setTypeFilter({ typeFilter: newFilter }));
                }}
              >
                {count} &nbsp;
                {displayName}
              </div>
            ))}
            {/* also add the tags for asset type that can be created but don't have asets yet */}
            {Object.entries(ontology.assetTypes)
              .sort((a, b) =>
                getAssetTypeName(ontology.assetTypes[a[0]], false).localeCompare(
                  getAssetTypeName(ontology.assetTypes[b[0]], false),
                ),
              )
              .map(([assetType, assetTypeOntology]) => {
                if (assetTypeOntology.userCanCreate && user.canWrite && !Object.keys(queryStats).includes(assetType)) {
                  return (
                    <div
                      key={assetType}
                      className={typeFilter === assetType ? styles.assetTypeTagActive : styles.createTypeTag}
                      onClick={() => {
                        let newFilter = '';
                        if (assetType !== typeFilter) newFilter = assetType;
                        dispatch(setTypeFilter({ typeFilter: newFilter }));
                      }}
                    >
                      {getAssetTypeName(ontology.assetTypes[assetType], false)}
                    </div>
                  );
                }
              })}
          </div>
        )}
      </div>
      <div key="search-filter-separator" className={styles.separator} />
    </>
  );
};

export default SearchFilter;
